/**
 * The planner's account menu, top right (Ed, 2026-09-24; OMON-349 parity
 * with app.carer's identity menu). The words live in account-summary.ts;
 * this module wires the DOM: fill the header when claims load, open and
 * close the menu, and point Support at a pre-filled email.
 *
 * Change password and Sign out keep their original element ids and
 * handlers (account.ts, auth.ts); they only moved from the left rail.
 */
import { doc, getDoc } from "firebase/firestore";

import { auth, db, appEnv } from "./firebase";
import { accountSummary, supportHref, type AccountFacts } from "./account-summary";
import { formatPersonName } from "./admin/person-name";
import type { AuthClaims } from "./admin/claims";

declare const __APP_VERSION__: string;

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T | null;

let wired = false;
/** The person whose name the header shows, so a sign-in as someone else
 *  never shows the previous person's name while the new one loads. */
let shownFor: string | null = null;

function setOpen(open: boolean): void {
  const btn = $<HTMLButtonElement>("accountBtn");
  const menu = $<HTMLElement>("accountMenu");
  if (!btn || !menu) return;
  menu.hidden = !open;
  btn.setAttribute("aria-expanded", String(open));
  if (open) menu.querySelector<HTMLElement>(".acct-item")?.focus();
}

function wireOnce(): void {
  if (wired) return;
  const btn = $<HTMLButtonElement>("accountBtn");
  const menu = $<HTMLElement>("accountMenu");
  if (!btn || !menu) return;
  wired = true;
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    setOpen(menu.hidden);
  });
  // Choosing anything closes the menu first; the item's own handler
  // (password dialog, sign-out, mail link) then runs as before.
  menu.querySelectorAll(".acct-item").forEach((item) =>
    item.addEventListener("click", () => setOpen(false)));
  document.addEventListener("click", (e) => {
    if (!menu.hidden && !menu.contains(e.target as Node)) setOpen(false);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !menu.hidden) {
      setOpen(false);
      btn.focus();
    }
  });
}

function paint(f: AccountFacts): void {
  const s = accountSummary(f);
  const title = $("acctTitle");
  const email = $("acctEmail");
  const meta = $("acctMeta");
  const version = $("acctVersion");
  const support = $<HTMLAnchorElement>("acctSupport");
  if (title) title.textContent = s.title;
  if (email) {
    email.textContent = s.email ?? "";
    email.hidden = !s.email;
  }
  if (meta) meta.textContent = s.meta;
  if (version) version.textContent = `Planner v${f.version}`;
  if (support) support.href = supportHref(f);
}

/** Called whenever claims change (sign-in, sign-out, a refreshed token). */
export function refreshAccountMenu(claims: AuthClaims | null): void {
  wireOnce();
  setOpen(false);
  const facts: AccountFacts = {
    name: null,
    email: auth.currentUser?.email ?? null,
    role: claims?.role ?? null,
    env: appEnv,
    version: __APP_VERSION__,
  };
  const personId = claims?.personDocId ?? null;
  shownFor = personId;
  paint(facts);
  if (!personId) return;
  // The name is one read, done after the header is already showing the
  // email, so the menu is never blank while it loads.
  void getDoc(doc(db, "_person", personId))
    .then((snap) => {
      if (shownFor !== personId) return; // someone else signed in meanwhile
      paint({ ...facts, name: formatPersonName(snap.exists() ? snap : null) });
    })
    .catch(() => {
      /* the email stays as the title: honest, just less friendly */
    });
}
